import { ChapterAudioDetail, ChapterData } from 'src/interfaces'
import { BookAudioItem, ChapterEditLanguageSection } from 'src/interfaces'
import { getVariantLabel } from 'src/utils/bookAudioDialects'
import { getLanguageLabel } from 'src/utils/bookLanguageLabels'
import {
	languageSortOrder,
	resolveHighlightStatus,
	resolveLanguageDocState,
	shouldShowAudioForDetail,
} from 'src/utils/chapterSlotStatus'

export const audioDetailToBookAudioItem = (
	detail: ChapterAudioDetail,
	chapterId?: string,
): BookAudioItem => ({
	id: detail.id,
	chapter_id: detail.chapter_id ?? chapterId ?? null,
	language: detail.language ?? detail.language_variant ?? null,
	language_variant: detail.language_variant ?? null,
	url: detail.url ?? null,
	filename:
		detail.url?.split('/').pop() ??
		detail.language_variant ??
		detail.language ??
		undefined,
	status: detail.status ?? null,
})

export const buildChapterEditSections = (chapter: ChapterData) => {
	const audioDetails = chapter.audio_details ?? []

	const grouped = new Map<string, ChapterAudioDetail[]>()
	audioDetails.forEach((detail) => {
		const lang = detail.language ?? 'unknown'
		const list = grouped.get(lang) ?? []
		list.push(detail)
		grouped.set(lang, list)
	})

	const sortedLanguages = Array.from(grouped.keys()).sort((a, b) => {
		const orderDiff = languageSortOrder(a) - languageSortOrder(b)
		if (orderDiff !== 0) return orderDiff
		return a.localeCompare(b)
	})

	const languageSections: ChapterEditLanguageSection[] = sortedLanguages.map(
		(lang) => {
			const details = grouped.get(lang) ?? []
			const { docUrl, docProcessing } = resolveLanguageDocState(
				details,
				lang === 'en' ? chapter.original_doc_url : null,
			)

			const docBookAudioId = details[0]?.id ?? null

			const audioVariants = details
				.slice()
				.sort((a, b) =>
					String(b.language_variant ?? '').localeCompare(
						String(a.language_variant ?? ''),
					),
				)
				.map((detail) => {
					const showAudio = shouldShowAudioForDetail(detail)
					const audio = showAudio
						? audioDetailToBookAudioItem(detail, chapter.id)
						: null

					return {
						dialectCode: detail.language_variant ?? detail.language ?? detail.id,
						label: getVariantLabel(detail.language_variant, detail.language),
						audio,
						bookAudioId: detail.id,
						language: detail.language,
						language_variant: detail.language_variant,
						status: detail.status,
						highlightStatus: resolveHighlightStatus(detail.alignment),
					}
				})

			return {
				language: lang,
				label: getLanguageLabel(lang),
				docUrl,
				docProcessing,
				docBookAudioId,
				protectDocActions: lang === 'en',
				audios: details
					.filter((item) => item.url && shouldShowAudioForDetail(item))
					.map((item) => audioDetailToBookAudioItem(item, chapter.id)),
				bookAudioId: null,
				audioVariants,
			}
		},
	)

	return { languageSections }
}
